// FormBuilder 컴포넌트의 폼 상태 관리 관련 유틸리티 함수

/**
 * 고유 ID 생성 함수
 */
export const generateId = (prefix = "item") => {
  return `${prefix}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
};

/**
 * 빈 폼 구조를 생성하는 함수
 * @returns {Object} - 기본 폼 구조
 */
export const createEmptyFormStructure = () => {
  const pageId = generateId("page");
  const sectionId = generateId("section");
  const rowId = generateId("row");

  return {
    id: generateId("form"),
    title: "새 폼",
    description: "새 폼 설명",
    pages: [
      {
        id: pageId,
        title: "페이지 1",
        sections: [
          {
            id: sectionId,
            title: "섹션 1",
            rows: [
              {
                id: rowId,
                layout: "full",
                fields: [],
              },
            ],
          },
        ],
      },
    ],
  };
};

/**
 * 새 페이지를 폼 구조에 추가하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @returns {Object} - 업데이트된 폼 구조
 */
export const addNewPage = (formStructure) => {
  const newPageId = `page_${Date.now()}`;
  const newSectionId = `section_${Date.now()}`;
  const newRowId = `row_${Date.now()}`;

  const newPage = {
    id: newPageId,
    title: `페이지 ${formStructure.pages.length + 1}`,
    sections: [
      {
        id: newSectionId,
        title: "섹션 1",
        rows: [
          {
            id: newRowId,
            layout: "full",
            fields: [],
          },
        ],
      },
    ],
  };

  return {
    ...formStructure,
    pages: [...formStructure.pages, newPage],
  };
};

/**
 * 특정 페이지를 폼 구조에서 삭제하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} pageId - 삭제할 페이지의 ID
 * @returns {Object} - 업데이트된 폼 구조
 */
export const deletePage = (formStructure, pageId) => {
  if (formStructure.pages.length <= 1) {
    return formStructure; // 최소 1개의 페이지는 남겨두어야 함
  }

  return {
    ...formStructure,
    pages: formStructure.pages.filter((page) => page.id !== pageId),
  };
};

/**
 * 페이지의 제목을 업데이트하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} pageId - 업데이트할 페이지의 ID
 * @param {string} newTitle - 새 제목
 * @returns {Object} - 업데이트된 폼 구조
 */
export const updatePageTitle = (formStructure, pageId, newTitle) => {
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) =>
      page.id === pageId ? { ...page, title: newTitle } : page
    ),
  };
};

/**
 * 섹션을 페이지에 추가하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} pageId - 섹션을 추가할 페이지의 ID
 * @returns {Object} - 업데이트된 폼 구조
 */
export const addSectionToPage = (formStructure, pageId) => {
  const newSectionId = `section_${Date.now()}`;
  const newRowId = `row_${Date.now()}`;

  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => {
      if (page.id === pageId) {
        const newSection = {
          id: newSectionId,
          title: `섹션 ${page.sections.length + 1}`,
          rows: [
            {
              id: newRowId,
              layout: "full",
              fields: [],
            },
          ],
        };

        return {
          ...page,
          sections: [...page.sections, newSection],
        };
      }
      return page;
    }),
  };
};

/**
 * 섹션을 삭제하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} sectionId - 삭제할 섹션의 ID
 * @returns {Object} - 업데이트된 폼 구조
 */
export const deleteSection = (formStructure, sectionId) => {
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => {
      if (page.sections.some((section) => section.id === sectionId)) {
        if (page.sections.length <= 1) {
          return page; // 페이지당 최소 1개의 섹션은 남겨두어야 함
        }

        return {
          ...page,
          sections: page.sections.filter((section) => section.id !== sectionId),
        };
      }
      return page;
    }),
  };
};

/**
 * 섹션의 제목을 업데이트하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} sectionId - 업데이트할 섹션의 ID
 * @param {string} newTitle - 새 제목
 * @returns {Object} - 업데이트된 폼 구조
 */
export const updateSectionTitle = (formStructure, sectionId, newTitle) => {
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) =>
        section.id === sectionId ? { ...section, title: newTitle } : section
      ),
    })),
  };
};

/**
 * 행을 섹션에 추가하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} sectionId - 행을 추가할 섹션의 ID
 * @param {string|Object} layoutOrRow - 행의 레이아웃 타입 ('full' 또는 'two-column') 또는 행 객체
 * @returns {Object} - 업데이트된 폼 구조
 */
export const addRowToSection = (
  formStructure,
  sectionId,
  layoutOrRow = "full"
) => {
  // layoutOrRow 가 객체이면 그대로 사용, 문자열이면 새로 생성
  const newRowId = `row_${Date.now()}`;
  const newRow =
    typeof layoutOrRow === "object"
      ? {
          id: layoutOrRow.id || newRowId,
          layout: layoutOrRow.layout || "full",
          fields: layoutOrRow.fields || [],
        }
      : { id: newRowId, layout: layoutOrRow, fields: [] };

  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            rows: [...section.rows, newRow],
          };
        }
        return section;
      }),
    })),
  };
};

/**
 * 행을 삭제하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} rowId - 삭제할 행의 ID
 * @returns {Object} - 업데이트된 폼 구조
 */
export const deleteRow = (formStructure, rowId) => {
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => {
        if (section.rows.some((row) => row.id === rowId)) {
          if (section.rows.length <= 1) {
            return section; // 섹션당 최소 1개의 행은 남겨두어야 함
          }

          return {
            ...section,
            rows: section.rows.filter((row) => row.id !== rowId),
          };
        }
        return section;
      }),
    })),
  };
};

/**
 * 행의 레이아웃을 업데이트하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} rowId - 업데이트할 행의 ID
 * @param {string} newLayout - 새 레이아웃 ('full' 또는 'two-column')
 * @returns {Object} - 업데이트된 폼 구조
 */
export const updateRowLayout = (formStructure, rowId, newLayout) => {
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => {
          if (row.id === rowId) {
            // 레이아웃이 변경되면 필드도 조정
            let updatedFields = [...row.fields];
            if (newLayout === "full" && updatedFields.length > 1) {
              updatedFields = [updatedFields[0]]; // 전체 너비 행은 필드 1개만 허용
            }

            return {
              ...row,
              layout: newLayout,
              fields: updatedFields,
            };
          }
          return row;
        }),
      })),
    })),
  };
};

/**
 * 새 필드 객체를 생성합니다
 * @param {string} fieldType - 필드 타입 (text, number, select 등)
 * @returns {Object} 새 필드 객체
 */
export const createNewField = (fieldType) => {
  return {
    id: `field_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    type: fieldType || "text",
    label: `새 ${fieldType || "텍스트"} 필드`,
    required: false,
    placeholder: "",
    options:
      fieldType === "select" || fieldType === "radio"
        ? [
            { label: "옵션 1", value: "option1" },
            { label: "옵션 2", value: "option2" },
          ]
        : [],
    defaultValue: "",
    validation: null,
    description: "",
    dependsOn: null,
  };
};

/**
 * 필드를 행에 추가합니다
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} rowId - 필드를 추가할 행 ID
 * @param {Object} field - 추가할 필드 객체
 * @returns {Object} 업데이트된 폼 구조
 */
export const addFieldToRow = (formStructure, rowId, field) => {
  if (!formStructure || !rowId || !field) {
    console.error(
      "필드를 행에 추가하려면 formStructure, rowId, field가 필요합니다"
    );
    return formStructure;
  }

  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => {
          if (row.id === rowId) {
            // 행 레이아웃 확인 (full 또는 two-column)
            const layout = row.layout || "full";

            // 필드 배치 처리
            let updatedFields = [...row.fields];

            // 이미 2개의 필드가 있고 two-column 레이아웃인 경우 추가하지 않음
            if (layout === "two-column" && updatedFields.length >= 2) {
              console.warn("2열 행에는 최대 2개의 필드만 추가할 수 있습니다");
              return row;
            }

            // 필드 추가
            updatedFields.push(field);

            return {
              ...row,
              fields: updatedFields,
            };
          }
          return row;
        }),
      })),
    })),
  };
};

/**
 * 필드를 삭제하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} fieldId - 삭제할 필드의 ID
 * @returns {Object} - 업데이트된 폼 구조
 */
export const deleteField = (formStructure, fieldId) => {
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => {
          if (row.fields.some((field) => field.id === fieldId)) {
            return {
              ...row,
              fields: row.fields.filter((field) => field.id !== fieldId),
            };
          }
          return row;
        }),
      })),
    })),
  };
};

/**
 * 필드의 속성을 업데이트하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} fieldId - 업데이트할 필드의 ID
 * @param {Object} updates - 업데이트할 속성들
 * @returns {Object} - 업데이트된 폼 구조
 */
export const updateFieldProperties = (formStructure, fieldId, updates) => {
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
          ...row,
          fields: row.fields.map((field) =>
            field.id === fieldId ? { ...field, ...updates } : field
          ),
        })),
      })),
    })),
  };
};

/**
 * 필드의 의존성을 업데이트하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} fieldId - 업데이트할 필드의 ID
 * @param {Object} dependency - 새 의존성 객체
 * @returns {Object} - 업데이트된 폼 구조
 */
export const updateFieldDependency = (formStructure, fieldId, dependency) => {
  return updateFieldProperties(formStructure, fieldId, { dependency });
};

/**
 * 필드의 의존성을 제거하는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} fieldId - 업데이트할 필드의 ID
 * @returns {Object} - 업데이트된 폼 구조
 */
export const removeFieldDependency = (formStructure, fieldId) => {
  return updateFieldProperties(formStructure, fieldId, { dependency: null });
};

/**
 * 폼의 모든 필드 ID를 가져오는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @returns {Array<Object>} - ID와 레이블을 포함한 필드 객체 배열
 */
export const getAllFieldIds = (formStructure) => {
  const fields = [];

  if (!formStructure || !formStructure.pages) {
    return fields;
  }

  for (const page of formStructure.pages) {
    for (const section of page.sections) {
      for (const row of section.rows) {
        for (const field of row.fields) {
          fields.push({
            id: field.id,
            label:
              typeof field.label === "string"
                ? field.label
                : `필드 (${field.id})`,
            type: typeof field.type === "string" ? field.type : "unknown",
          });
        }
      }
    }
  }

  return fields;
};

/**
 * 폼의 모든 필드 ID를 문자열로 가져오는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @returns {Array<string>} - 필드 ID 문자열 배열
 */
export const getAllFieldIdsAsStrings = (formStructure) => {
  const fields = [];

  if (!formStructure || !formStructure.pages) {
    return fields;
  }

  for (const page of formStructure.pages) {
    for (const section of page.sections) {
      for (const row of section.rows) {
        for (const field of row.fields) {
          if (field.id) {
            fields.push(field.id);
          }
        }
      }
    }
  }

  return fields;
};

/**
 * 폼의 모든 필드 정보를 안전하게 가져오는 함수
 * @param {Object} formStructure - 현재 폼 구조
 * @returns {Array<{id: string, label: string, type: string}>} - 필드 정보 객체 배열
 */
export const getAllFieldsInfo = (formStructure) => {
  const fields = [];

  if (!formStructure || !formStructure.pages) {
    return fields;
  }

  for (const page of formStructure.pages) {
    for (const section of page.sections) {
      for (const row of section.rows) {
        for (const field of row.fields) {
          if (field.id) {
            fields.push({
              id: field.id,
              label:
                typeof field.label === "string"
                  ? field.label
                  : `필드 (${field.id})`,
              type: typeof field.type === "string" ? field.type : "unknown",
            });
          }
        }
      }
    }
  }

  return fields;
};

/**
 * formStructure에서 특정 요소를 찾습니다
 * @param {Object} formStructure - 폼 구조
 * @param {string} elementType - 찾을 요소 타입 (page, section, row, field)
 * @param {string} elementId - 찾을 요소 ID
 * @returns {Object|null} 찾은 요소 또는 null
 */
export const findElementInFormStructure = (
  formStructure,
  elementType,
  elementId
) => {
  if (!formStructure || !elementType || !elementId) {
    return null;
  }

  if (elementType === "form" && formStructure.id === elementId) {
    return formStructure;
  }

  // 페이지 검색
  if (elementType === "page") {
    return formStructure.pages.find((page) => page.id === elementId) || null;
  }

  // 섹션 검색
  if (elementType === "section") {
    for (const page of formStructure.pages) {
      const section = page.sections.find((section) => section.id === elementId);
      if (section) return section;
    }
    return null;
  }

  // 행 검색
  if (elementType === "row") {
    for (const page of formStructure.pages) {
      for (const section of page.sections) {
        const row = section.rows.find((row) => row.id === elementId);
        if (row) return row;
      }
    }
    return null;
  }

  // 필드 검색
  if (elementType === "field") {
    for (const page of formStructure.pages) {
      for (const section of page.sections) {
        for (const row of section.rows) {
          const field = row.fields.find((field) => field.id === elementId);
          if (field) return field;
        }
      }
    }
    return null;
  }

  return null;
};

/**
 * 특정 페이지 찾기
 */
export const findPageById = (formStructure, pageId) => {
  if (!formStructure || !formStructure.pages || !pageId) {
    return null;
  }

  return formStructure.pages.find((page) => page.id === pageId) || null;
};

/**
 * 특정 섹션의 상위 페이지 ID 찾기
 */
export const findPageIdForSection = (formStructure, sectionId) => {
  if (!formStructure || !formStructure.pages || !sectionId) {
    return null;
  }

  for (const page of formStructure.pages) {
    const sectionExists = page.sections.some(
      (section) => section.id === sectionId
    );
    if (sectionExists) return page.id;
  }

  return null;
};

/**
 * 특정 행의 상위 섹션 ID 찾기
 */
export const findSectionIdForRow = (formStructure, rowId) => {
  if (!formStructure || !formStructure.pages || !rowId) {
    return null;
  }

  for (const page of formStructure.pages) {
    for (const section of page.sections) {
      const rowExists = section.rows.some((row) => row.id === rowId);
      if (rowExists) return section.id;
    }
  }

  return null;
};

/**
 * 특정 필드의 상위 행 ID 찾기
 */
export const findRowIdForField = (formStructure, fieldId) => {
  if (!formStructure || !formStructure.pages || !fieldId) {
    return null;
  }

  for (const page of formStructure.pages) {
    for (const section of page.sections) {
      for (const row of section.rows) {
        const fieldExists = row.fields.some((field) => field.id === fieldId);
        if (fieldExists) return row.id;
      }
    }
  }

  return null;
};

/**
 * 특정 요소의 경로 찾기 (페이지 인덱스, 섹션 인덱스, 행 인덱스, 필드 인덱스)
 */
export const findElementPath = (formStructure, elementType, elementId) => {
  if (!formStructure || !elementType || !elementId) {
    return {};
  }

  // 페이지 경로
  if (elementType === "page") {
    const pageIndex = formStructure.pages.findIndex(
      (page) => page.id === elementId
    );
    return { pageIndex: pageIndex !== -1 ? pageIndex : null };
  }

  // 섹션 경로
  if (elementType === "section") {
    for (
      let pageIndex = 0;
      pageIndex < formStructure.pages.length;
      pageIndex++
    ) {
      const page = formStructure.pages[pageIndex];
      const sectionIndex = page.sections.findIndex(
        (section) => section.id === elementId
      );

      if (sectionIndex !== -1) {
        return { pageIndex, sectionIndex };
      }
    }
  }

  // 행 경로
  if (elementType === "row") {
    for (
      let pageIndex = 0;
      pageIndex < formStructure.pages.length;
      pageIndex++
    ) {
      const page = formStructure.pages[pageIndex];

      for (
        let sectionIndex = 0;
        sectionIndex < page.sections.length;
        sectionIndex++
      ) {
        const section = page.sections[sectionIndex];
        const rowIndex = section.rows.findIndex((row) => row.id === elementId);

        if (rowIndex !== -1) {
          return { pageIndex, sectionIndex, rowIndex };
        }
      }
    }
  }

  // 필드 경로
  if (elementType === "field") {
    for (
      let pageIndex = 0;
      pageIndex < formStructure.pages.length;
      pageIndex++
    ) {
      const page = formStructure.pages[pageIndex];

      for (
        let sectionIndex = 0;
        sectionIndex < page.sections.length;
        sectionIndex++
      ) {
        const section = page.sections[sectionIndex];

        for (let rowIndex = 0; rowIndex < section.rows.length; rowIndex++) {
          const row = section.rows[rowIndex];
          const fieldIndex = row.fields.findIndex(
            (field) => field.id === elementId
          );

          if (fieldIndex !== -1) {
            return { pageIndex, sectionIndex, rowIndex, fieldIndex };
          }
        }
      }
    }
  }

  return {};
};

/**
 * 폼 구조에서 요소 업데이트
 */
export const updateElementInFormStructure = (
  formStructure,
  elementType,
  elementId,
  updates
) => {
  if (!formStructure || !elementType || !elementId || !updates) {
    return formStructure;
  }

  // 폼 업데이트
  if (elementType === "form") {
    return { ...formStructure, ...updates };
  }

  // 페이지 업데이트
  if (elementType === "page") {
    return {
      ...formStructure,
      pages: formStructure.pages.map((page) =>
        page.id === elementId ? { ...page, ...updates } : page
      ),
    };
  }

  // 섹션 업데이트
  if (elementType === "section") {
    return {
      ...formStructure,
      pages: formStructure.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) =>
          section.id === elementId ? { ...section, ...updates } : section
        ),
      })),
    };
  }

  // 행 업데이트
  if (elementType === "row") {
    return {
      ...formStructure,
      pages: formStructure.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) => ({
          ...section,
          rows: section.rows.map((row) =>
            row.id === elementId ? { ...row, ...updates } : row
          ),
        })),
      })),
    };
  }

  // 필드 업데이트
  if (elementType === "field") {
    return {
      ...formStructure,
      pages: formStructure.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) => ({
          ...section,
          rows: section.rows.map((row) => ({
            ...row,
            fields: row.fields.map((field) =>
              field.id === elementId ? { ...field, ...updates } : field
            ),
          })),
        })),
      })),
    };
  }

  return formStructure;
};

/**
 * 페이지를 이동합니다 (동일 폼 내 페이지 재정렬)
 */
export const movePage = (formStructure, pageId, destIndex) => {
  if (!formStructure || !formStructure.pages) return formStructure;
  const pages = [...formStructure.pages];
  const currentIndex = pages.findIndex((p) => p.id === pageId);
  if (currentIndex === -1 || destIndex === currentIndex) return formStructure;
  const [page] = pages.splice(currentIndex, 1);
  pages.splice(destIndex, 0, page);
  return { ...formStructure, pages };
};

/**
 * 섹션을 다른 페이지(혹은 동일 페이지 내)로 이동
 */
export const moveSection = (
  formStructure,
  sectionId,
  destPageId,
  destIndex = null
) => {
  if (!formStructure) return formStructure;
  let movingSection = null;
  const pagesAfterRemoval = formStructure.pages.map((p) => {
    const idx = p.sections.findIndex((s) => s.id === sectionId);
    if (idx !== -1) {
      movingSection = p.sections[idx];
      const newSections = [...p.sections];
      newSections.splice(idx, 1);
      return { ...p, sections: newSections };
    }
    return p;
  });
  if (!movingSection) return formStructure;
  const pagesAfterInsert = pagesAfterRemoval.map((p) => {
    if (p.id === destPageId) {
      const newSections = [...p.sections];
      const insertIdx = destIndex !== null ? destIndex : newSections.length;
      newSections.splice(insertIdx, 0, movingSection);
      return { ...p, sections: newSections };
    }
    return p;
  });
  return { ...formStructure, pages: pagesAfterInsert };
};

/**
 * 행(Row)를 다른 섹션으로 이동
 */
export const moveRow = (
  formStructure,
  rowId,
  destSectionId,
  destIndex = null
) => {
  if (!formStructure) return formStructure;
  let movingRow = null;
  const pagesAfterRemoval = formStructure.pages.map((p) => ({
    ...p,
    sections: p.sections.map((s) => {
      const idx = s.rows.findIndex((r) => r.id === rowId);
      if (idx !== -1) {
        movingRow = s.rows[idx];
        const newRows = [...s.rows];
        newRows.splice(idx, 1);
        return { ...s, rows: newRows };
      }
      return s;
    }),
  }));
  if (!movingRow) return formStructure;
  const pagesAfterInsert = pagesAfterRemoval.map((p) => ({
    ...p,
    sections: p.sections.map((s) => {
      if (s.id === destSectionId) {
        const newRows = [...s.rows];
        const insertIdx = destIndex !== null ? destIndex : newRows.length;
        newRows.splice(insertIdx, 0, movingRow);
        return { ...s, rows: newRows };
      }
      return s;
    }),
  }));
  return { ...formStructure, pages: pagesAfterInsert };
};

/**
 * 필드를 다른 행으로 이동 (2열 지원)
 */
export const moveField = (
  formStructure,
  fieldId,
  destRowId,
  destColumn = null,
  destIndex = null
) => {
  if (!formStructure) return formStructure;
  let movingField = null;
  const pagesAfterRemoval = formStructure.pages.map((p) => ({
    ...p,
    sections: p.sections.map((s) => ({
      ...s,
      rows: s.rows.map((r) => {
        const idx = r.fields.findIndex((f) => f.id === fieldId);
        if (idx !== -1) {
          movingField = r.fields[idx];
          const newFields = [...r.fields];
          newFields.splice(idx, 1);
          return { ...r, fields: newFields };
        }
        return r;
      }),
    })),
  }));
  if (!movingField) return formStructure;
  // column 변경
  if (destColumn) movingField = { ...movingField, column: destColumn };
  const pagesAfterInsert = pagesAfterRemoval.map((p) => ({
    ...p,
    sections: p.sections.map((s) => ({
      ...s,
      rows: s.rows.map((r) => {
        if (r.id === destRowId) {
          const newFields = [...r.fields];
          const insertIdx = destIndex !== null ? destIndex : newFields.length;
          newFields.splice(insertIdx, 0, movingField);
          return { ...r, fields: newFields };
        }
        return r;
      }),
    })),
  }));
  return { ...formStructure, pages: pagesAfterInsert };
};

/**
 * 필드 ID를 변경합니다.
 * @param {Object} formStructure - 현재 폼 구조
 * @param {string} oldId - 기존 필드 ID
 * @param {string} newId - 새 필드 ID
 * @returns {Object} 업데이트된 폼 구조
 */
export const renameFieldId = (formStructure, oldId, newId) => {
  if (!oldId || !newId || oldId === newId) return formStructure;
  return {
    ...formStructure,
    pages: formStructure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        rows: section.rows.map((row) => ({
          ...row,
          fields: row.fields.map((field) =>
            field.id === oldId ? { ...field, id: newId } : field
          ),
        })),
      })),
    })),
  };
};

/**
 * 특정 행을 찾는 함수
 * @param {Object} formStructure - 폼 구조
 * @param {string} rowId - 찾을 행의 ID
 * @returns {Object|null} 찾은 행 또는 null
 */
export const findRowById = (formStructure, rowId) => {
  for (const page of formStructure.pages) {
    for (const section of page.sections) {
      const row = section.rows.find((r) => r.id === rowId);
      if (row) return row;
    }
  }
  return null;
};
